import React from 'react'
import { AdaptiveCarousel, AdaptiveCarouselSlide, Column, Row, SwanI18nProvider } from '@vp/swan'
import type { TileSkin } from '../../types/sharedTypes'
import type { Tile } from '../../types/homepageFeatureTileRow'
import TileComponent from './Tile'

const ADAPTIVE_CAROUSEL_NEXT_KEY = 'adaptive-carousel-slide-navigation-internal.accessible-text-for-next-button'
const ADAPTIVE_CAROUSEL_PREVIOUS_KEY = 'adaptive-carousel-slide-navigation-internal.accessible-text-for-previous-button'
const ADAPTIVE_CAROUSEL_SLIDE_PICKER_KEY = 'adaptive-carousel-slide-picker-internal.accessible-text-for-button'

export type ScreenToSlidesMap = Record<string, number>

interface AdaptiveCarouselTilesListProps {
  tiles: Array<Tile>
  visibleTiles: number
  slidesToShow: ScreenToSlidesMap
  tileSkin?: TileSkin
  loadType: string
  enableCampaignCallouts: boolean
  showPrice: boolean
  trackingPageZone: string
  id: string
  sectionHeader: string
  carouselA11yText: {
    a11yCarouselLabel?: string
    a11yCarouselRole?: string
    a11yCarouselSlide?: string
    a11yCarouselNext?: string
    a11yCarouselPrevious?: string
    a11yCarouselVisible?: string
    a11yCarouselDotsTemplate?: string
  }
  locale: string
}

const AdaptiveCarouselTilesList = (props: AdaptiveCarouselTilesListProps) => {
  const { tiles, slidesToShow, tileSkin, loadType, enableCampaignCallouts, showPrice, trackingPageZone, id, sectionHeader, carouselA11yText, locale } = props

  const { a11yCarouselLabel, a11yCarouselRole, a11yCarouselSlide, a11yCarouselNext, a11yCarouselPrevious, a11yCarouselDotsTemplate } = carouselA11yText

  const carouselName = a11yCarouselLabel ? `${a11yCarouselLabel}: ${sectionHeader}` : undefined

  const carouselTranslations: Record<string, string> = {}

  if (a11yCarouselNext) {
    carouselTranslations[ADAPTIVE_CAROUSEL_NEXT_KEY] = `${sectionHeader}: ${a11yCarouselNext}`
  }

  if (a11yCarouselPrevious) {
    carouselTranslations[ADAPTIVE_CAROUSEL_PREVIOUS_KEY] = `${sectionHeader}: ${a11yCarouselPrevious}`
  }

  if (a11yCarouselDotsTemplate) {
    carouselTranslations[ADAPTIVE_CAROUSEL_SLIDE_PICKER_KEY] = `${sectionHeader}: ${a11yCarouselDotsTemplate}`
  }

  return (
    <Row>
      <Column span={12}>
        <SwanI18nProvider locale={locale as React.ComponentProps<typeof SwanI18nProvider>['locale']} translations={carouselTranslations}>
          <AdaptiveCarousel id={id.replaceAll(' ', '')} aria-label={carouselName as string} aria-roledescription={a11yCarouselRole} slidesToShow={slidesToShow}>
            {tiles.map((tile, index) => (
              <AdaptiveCarouselSlide data-testid="slick-item" key={`${tile.contentful_id ?? 'tile'}-${index}`} aria-roledescription={a11yCarouselSlide}>
                <TileComponent
                  loadType={loadType}
                  data={tile}
                  position={index + 1}
                  trackingPageZone={trackingPageZone}
                  enableCampaignCallouts={enableCampaignCallouts}
                  showPrice={showPrice}
                  tileSkin={tileSkin}
                  locale={locale}
                  marginBottom={0}
                />
              </AdaptiveCarouselSlide>
            ))}
          </AdaptiveCarousel>
        </SwanI18nProvider>
      </Column>
    </Row>
  )
}

export default AdaptiveCarouselTilesList
